import { useMemo, useState } from "react";
import { Bot, Check, Loader2, PlayCircle, Plus, Power, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useAiChat,
    useApplyFilter,
    useCreateFilter,
    useDeleteFilter,
    useFilters,
    useFolders,
    useUpdateFilter
} from "@/api/hooks";
import type { AiFilterSuggestion, FilterRow, UpsertFilterPayload } from "@/api/mailApi";

interface Condition {
    field: "from" | "to" | "subject" | "body";
    op: "contains" | "equals" | "startsWith" | "endsWith";
    value: string;
}

interface Action {
    type: "move" | "label" | "markRead" | "markUnread" | "star" | "stop";
    folderId?: string;
    labelId?: string;
}

type MatchMode = "all" | "any";

function parse(json: string, fallback: unknown): unknown {
    try { return JSON.parse(json); } catch { return fallback; }
}

/** ConditionsJson accepts two shapes: legacy bare array (= match all) and the
 *  wrapper {"match":"any"|"all","conditions":[…]} the BFF now emits. */
function parseConditions(json: string): { match: MatchMode; conditions: Condition[] } {
    const raw = parse(json, []) as unknown;
    if (Array.isArray(raw)) {
        return { match: "all", conditions: raw as Condition[] };
    }
    const wrapper = raw as { match?: string; conditions?: Condition[] };
    return {
        match: wrapper.match === "any" ? "any" : "all",
        conditions: wrapper.conditions ?? []
    };
}

const EMPTY_CONDITION: Condition = { field: "from", op: "contains", value: "" };
const EMPTY_ACTION: Action = { type: "move", folderId: "archive" };

export function FiltersPage() {
    const filters = useFilters();
    const create = useCreateFilter();

    const [drafting, setDrafting] = useState(false);
    const [aiDrafting, setAiDrafting] = useState(false);
    const [search, setSearch] = useState("");

    const allFilters = filters.data ?? [];
    const visibleFilters = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (q.length === 0) return allFilters;
        return allFilters.filter((f) =>
            f.name.toLowerCase().includes(q) ||
            f.conditionsJson.toLowerCase().includes(q)
        );
    }, [allFilters, search]);

    return (
        <div className="mx-auto max-w-3xl">
            <header className="mb-4 flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">Filters &amp; rules</h2>
                    <p className="text-sm text-muted-foreground">
                        When a message arrives that matches the conditions, run the actions —
                        e.g. file mail from a customer straight into its folder.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => { setAiDrafting((v) => !v); setDrafting(false); }}
                        disabled={aiDrafting}
                    >
                        <Bot className="h-4 w-4" /> Generate with AI
                    </Button>
                    <Button onClick={() => { setDrafting(true); setAiDrafting(false); }} disabled={drafting}>
                        <Plus className="h-4 w-4" /> New rule
                    </Button>
                </div>
            </header>

            {aiDrafting ? (
                <AiFilterPanel
                    sortOrder={filters.data?.length ?? 0}
                    onCreated={() => setAiDrafting(false)}
                    onCancel={() => setAiDrafting(false)}
                />
            ) : null}

            {drafting ? (
                <RuleEditor
                    initial={{
                        name: "New rule",
                        isEnabled: true,
                        sortOrder: filters.data?.length ?? 0,
                        conditionsJson: JSON.stringify([EMPTY_CONDITION]),
                        actionsJson: JSON.stringify([EMPTY_ACTION])
                    }}
                    submitLabel="Create"
                    pending={create.isPending}
                    onSubmit={(payload) =>
                        create.mutate(payload, { onSuccess: () => setDrafting(false) })
                    }
                    onCancel={() => setDrafting(false)}
                />
            ) : null}

            {allFilters.length > 0 ? (
                <div className="relative mb-3 max-w-sm">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <Input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter by name or condition"
                        aria-label="Filter rules"
                        className="h-8 pl-7 text-sm"
                    />
                </div>
            ) : null}

            {filters.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : allFilters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No rules yet.</p>
            ) : visibleFilters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No rules match &ldquo;{search}&rdquo;.</p>
            ) : (
                <ul className="space-y-2">
                    {visibleFilters.map((f) => (
                        <FilterItem key={f.id} filter={f} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function AiFilterPanel({
    sortOrder,
    onCreated,
    onCancel
}: {
    sortOrder: number;
    onCreated: () => void;
    onCancel: () => void;
}) {
    const aiChat = useAiChat();
    const create = useCreateFilter();
    const [prompt, setPrompt] = useState("");
    const [generated, setGenerated] = useState<AiFilterSuggestion | null>(null);
    const [genError, setGenError] = useState<string | null>(null);

    const generate = async () => {
        if (!prompt.trim() || aiChat.isPending) return;
        setGenError(null);
        setGenerated(null);
        try {
            const resp = await aiChat.mutateAsync({
                mode: "filter",
                messages: [{ role: "user", content: prompt }]
            });
            if (resp.generatedFilter) {
                setGenerated(resp.generatedFilter);
            } else {
                setGenError("AI did not return a filter structure. Try rephrasing your request.");
            }
        } catch (err) {
            setGenError(err instanceof Error ? err.message : "Generation failed. Check the Anthropic API key in the BFF.");
        }
    };

    const handleCreate = () => {
        if (!generated) return;
        create.mutate(
            {
                name: generated.name,
                isEnabled: true,
                sortOrder,
                conditionsJson: generated.conditionsJson,
                actionsJson: generated.actionsJson
            },
            { onSuccess: onCreated }
        );
    };

    // Inline preview of parsed conditions / actions
    const renderPreview = (cJson: string, aJson: string) => {
        try {
            const conds = JSON.parse(cJson) as Array<{ field: string; op: string; value: string }>;
            const acts = JSON.parse(aJson) as Array<{ type: string; folderId?: string; labelId?: string }>;
            return (
                <div className="space-y-0.5 text-xs text-foreground/80">
                    {conds.map((c, i) => (
                        <div key={i}>When <strong>{c.field}</strong> {c.op} &ldquo;{c.value}&rdquo;</div>
                    ))}
                    {acts.map((a, i) => (
                        <div key={i}>Then <strong>{a.type}</strong>{a.folderId ? ` → ${a.folderId}` : ""}{a.labelId ? ` → ${a.labelId}` : ""}</div>
                    ))}
                </div>
            );
        } catch {
            return <div className="text-xs text-muted-foreground italic">Filter rules generated</div>;
        }
    };

    return (
        <div className="mb-4 rounded-lg border bg-emerald-50/50 dark:bg-emerald-950/10 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                <Bot className="h-4 w-4 text-primary" />
                Generate rule with AI
            </div>
            <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={2}
                placeholder='e.g. "Move newsletters from Substack to a Newsletters folder"'
                disabled={aiChat.isPending}
                className="w-full resize-none rounded-md border px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void generate(); }
                }}
            />
            <div className="mt-2 flex gap-2">
                <Button size="sm" disabled={!prompt.trim() || aiChat.isPending} onClick={() => void generate()}>
                    {aiChat.isPending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Generating…</> : "Generate"}
                </Button>
                <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
            </div>
            {genError && (
                <p className="mt-1.5 text-xs text-destructive">{genError}</p>
            )}

            {generated && (
                <div className="mt-3 rounded-md border bg-card p-3 space-y-2">
                    <div className="font-semibold text-sm">{generated.name}</div>
                    {renderPreview(generated.conditionsJson, generated.actionsJson)}
                    <Button
                        size="sm"
                        className="w-full mt-1"
                        disabled={create.isPending}
                        onClick={handleCreate}
                    >
                        {create.isPending
                            ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            : <Plus className="mr-1 h-4 w-4" />}
                        Create this rule
                    </Button>
                </div>
            )}
        </div>
    );
}

function FilterItem({ filter }: { filter: FilterRow }) {
    const update = useUpdateFilter();
    const remove = useDeleteFilter();
    const apply = useApplyFilter();
    const [editing, setEditing] = useState(false);
    const [applyMsg, setApplyMsg] = useState<string | null>(null);

    const runApply = () => {
        setApplyMsg(null);
        apply.mutate(filter.id, {
            onSuccess: (r) =>
                setApplyMsg(r.moved > 0
                    ? `Moved ${r.moved} existing message${r.moved === 1 ? "" : "s"}.`
                    : r.matched > 0
                        ? `${r.matched} matched, but this rule has no move action.`
                        : "No existing messages matched."),
            onError: (e) => setApplyMsg(e instanceof Error ? e.message : "Apply failed.")
        });
    };

    const { match, conditions } = parseConditions(filter.conditionsJson);
    const actions = parse(filter.actionsJson, []) as Action[];

    if (editing) {
        return (
            <li>
                <RuleEditor
                    initial={filter}
                    submitLabel="Save"
                    pending={update.isPending}
                    onSubmit={(payload) =>
                        update.mutate(
                            { id: filter.id, payload },
                            { onSuccess: () => setEditing(false) }
                        )
                    }
                    onCancel={() => setEditing(false)}
                />
            </li>
        );
    }

    return (
        <li className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="font-medium">{filter.name}</h3>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                            filter.isEnabled ? "text-emerald-600" : "text-muted-foreground"
                        }`}>
                            {filter.isEnabled ? "enabled" : "disabled"}
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        <span className="font-semibold">When </span>
                        {conditions.length > 0
                            ? conditions.map((c) => `${c.field} ${c.op} "${c.value}"`).join(match === "any" ? " or " : " and ")
                            : "(no conditions)"}
                        <br />
                        <span className="font-semibold">Then </span>
                        {actions.length > 0
                            ? actions.map(describeAction).join(", ")
                            : "(no actions)"}
                    </p>
                </div>
                <div className="flex gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                            update.mutate({
                                id: filter.id,
                                payload: {
                                    name: filter.name,
                                    isEnabled: !filter.isEnabled,
                                    sortOrder: filter.sortOrder,
                                    conditionsJson: filter.conditionsJson,
                                    actionsJson: filter.actionsJson
                                }
                            })
                        }
                        aria-label={filter.isEnabled ? "Disable rule" : "Enable rule"}
                        title={filter.isEnabled ? "Disable" : "Enable"}
                    >
                        <Power className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={runApply}
                        disabled={apply.isPending}
                        title="Run this rule now over existing inbox mail"
                    >
                        {apply.isPending
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <PlayCircle className="h-3.5 w-3.5" />}
                        Apply now
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                        Edit
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete rule"
                        onClick={() => {
                            if (window.confirm(`Delete rule "${filter.name}"?`)) {
                                remove.mutate(filter.id);
                            }
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            {applyMsg ? (
                <p className="mt-2 text-xs text-muted-foreground">{applyMsg}</p>
            ) : null}
        </li>
    );
}

function describeAction(a: Action): string {
    switch (a.type) {
        case "move":
            return `move to ${a.folderId ?? "folder"}`;
        case "label":
            return `apply label ${a.labelId ?? "?"}`;
        case "markRead":
            return "mark as read";
        case "markUnread":
            return "mark as unread";
        case "star":
            return "flag as important";
        case "stop":
            return "stop processing more rules";
        default:
            return a.type;
    }
}

function RuleEditor({
    initial,
    submitLabel,
    pending,
    onSubmit,
    onCancel
}: {
    initial: UpsertFilterPayload;
    submitLabel: string;
    pending: boolean;
    onSubmit: (payload: UpsertFilterPayload) => void;
    onCancel: () => void;
}) {
    const [name, setName] = useState(initial.name);
    const [enabled, setEnabled] = useState(initial.isEnabled);
    const parsed = useMemo(() => parseConditions(initial.conditionsJson), [initial.conditionsJson]);
    const parsedActions = useMemo(
        () => (parse(initial.actionsJson, [EMPTY_ACTION]) as Action[]).map((a) => ({ ...a })),
        [initial.actionsJson]
    );
    const [match, setMatch] = useState<MatchMode>(parsed.match);
    const [conditions, setConditions] = useState<Condition[]>(
        parsed.conditions.length > 0 ? parsed.conditions.map((c) => ({ ...c })) : [{ ...EMPTY_CONDITION }]
    );
    const [actions, setActions] = useState<Action[]>(parsedActions);
    const folders = useFolders();

    const submit = () => {
        onSubmit({
            name: name.trim() || "Untitled rule",
            isEnabled: enabled,
            sortOrder: initial.sortOrder,
            conditionsJson: JSON.stringify({
                match,
                conditions: conditions.filter((c) => c.value.trim() !== "")
            }),
            actionsJson: JSON.stringify(actions)
        });
    };

    return (
        <div className="mb-4 rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
                <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    Enabled
                </label>
            </div>
            <div className="mb-3 rounded-md border bg-muted/30 p-3">
                <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    When
                    <select
                        value={match}
                        onChange={(e) => setMatch(e.target.value as MatchMode)}
                        aria-label="Match mode"
                        className="h-6 rounded-md border bg-background px-1 text-xs font-semibold normal-case"
                    >
                        <option value="all">all</option>
                        <option value="any">any</option>
                    </select>
                    of these match
                </h4>
                {conditions.map((c, i) => (
                    <div key={i} className="mb-1 flex items-center gap-2">
                        <select
                            value={c.field}
                            onChange={(e) =>
                                setConditions((prev) => {
                                    const next = [...prev];
                                    next[i] = { ...next[i]!, field: e.target.value as Condition["field"] };
                                    return next;
                                })
                            }
                            className="h-8 rounded-md border px-2 text-sm"
                        >
                            <option value="from">From</option>
                            <option value="to">To</option>
                            <option value="subject">Subject</option>
                            <option value="body">Body</option>
                        </select>
                        <select
                            value={c.op}
                            onChange={(e) =>
                                setConditions((prev) => {
                                    const next = [...prev];
                                    next[i] = { ...next[i]!, op: e.target.value as Condition["op"] };
                                    return next;
                                })
                            }
                            className="h-8 rounded-md border px-2 text-sm"
                        >
                            <option value="contains">contains</option>
                            <option value="equals">equals</option>
                            <option value="startsWith">starts with</option>
                            <option value="endsWith">ends with</option>
                        </select>
                        <Input
                            value={c.value}
                            onChange={(e) =>
                                setConditions((prev) => {
                                    const next = [...prev];
                                    next[i] = { ...next[i]!, value: e.target.value };
                                    return next;
                                })
                            }
                            placeholder="value"
                            className="h-8 flex-1"
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConditions((prev) => prev.filter((_, idx) => idx !== i))}
                            aria-label="Remove condition"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                ))}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConditions((prev) => [...prev, { ...EMPTY_CONDITION }])}
                >
                    <Plus className="h-3.5 w-3.5" />
                    Add condition
                </Button>
            </div>
            <div className="mb-3 rounded-md border bg-muted/30 p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Then do
                </h4>
                {actions.map((a, i) => (
                    <div key={i} className="mb-1 flex items-center gap-2">
                        <select
                            value={a.type}
                            onChange={(e) =>
                                setActions((prev) => {
                                    const next = [...prev];
                                    next[i] = { ...next[i]!, type: e.target.value as Action["type"] };
                                    return next;
                                })
                            }
                            className="h-8 rounded-md border px-2 text-sm"
                        >
                            <option value="move">Move to folder</option>
                            <option value="label">Apply label</option>
                            <option value="markRead">Mark as read</option>
                            <option value="markUnread">Mark as unread</option>
                            <option value="star">Flag as important</option>
                            <option value="stop">Stop processing more rules</option>
                        </select>
                        {a.type === "move" ? (
                            <select
                                value={a.folderId ?? ""}
                                onChange={(e) =>
                                    setActions((prev) => {
                                        const next = [...prev];
                                        next[i] = { ...next[i]!, folderId: e.target.value };
                                        return next;
                                    })
                                }
                                aria-label="Target folder"
                                className="h-8 flex-1 rounded-md border bg-background px-2 text-sm"
                            >
                                <option value="">Choose folder…</option>
                                {(folders.data ?? [])
                                    .filter((f) => f.kind !== "flagged")
                                    .map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {"\u00A0".repeat((f.depth ?? 0) * 3)}{f.name}
                                        </option>
                                    ))}
                                {a.folderId && !(folders.data ?? []).some((f) => f.id === a.folderId) ? (
                                    <option value={a.folderId}>{a.folderId}</option>
                                ) : null}
                            </select>
                        ) : a.type === "label" ? (
                            <Input
                                value={a.labelId ?? ""}
                                onChange={(e) =>
                                    setActions((prev) => {
                                        const next = [...prev];
                                        next[i] = { ...next[i]!, labelId: e.target.value };
                                        return next;
                                    })
                                }
                                placeholder="label id"
                                className="h-8 flex-1"
                            />
                        ) : (
                            <span className="flex-1 text-xs text-muted-foreground">No parameters</span>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setActions((prev) => prev.filter((_, idx) => idx !== i))}
                            aria-label="Remove action"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                ))}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActions((prev) => [...prev, { ...EMPTY_ACTION }])}
                >
                    <Plus className="h-3.5 w-3.5" />
                    Add action
                </Button>
            </div>
            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button onClick={submit} disabled={pending}>
                    {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    {submitLabel}
                </Button>
            </div>
        </div>
    );
}
